import React, { useState } from "react";
import { api, API_BASE } from "../lib/api";
import BirthDetailsForm from "../components/BirthDetailsForm";
import NorthIndianChart from "../components/NorthIndianChart";
import { LeadFooter } from "./JanamNakshatra";
import { Button } from "../components/ui/button";
import { Download, Sparkles, ChevronDown, ChevronRight } from "lucide-react";
import Seo from "../lib/Seo";
import { toast } from "sonner";

const K_FAQ = [
  { q: "What is a Vedic Kundali?", a: "A Vedic Kundali (also called Janam Kundali or Janma Patrika) is a birth chart showing the sidereal positions of the Sun, Moon, planets and lunar nodes at your exact time and place of birth. It serves as the map from which Jyotish (Vedic astrology) analysis begins." },
  { q: "Is this Kundali calculation free?", a: "Yes. YourAstroHub's Kundali generator is completely free. The tool uses Swiss Ephemeris for astronomical accuracy and the Lahiri (Chitrapaksha) ayanamsa recommended for Jyotish." },
  { q: "How accurate is the calculation?", a: "Astronomical accuracy is within ~1 arcsecond thanks to Swiss Ephemeris. Timezone and DST conversions are handled correctly using the IANA tz database. Jyotish interpretation itself is a traditional art and no guaranteed predictions are made." },
  { q: "Why does birth time matter so much?", a: "The Ascendant (Lagna) moves ~1° every four minutes. Even a 10-minute error can shift your Lagna sign, house cusps and pada — completely altering the chart's meaning." },
  { q: "What does the D9 (Navamsa) chart show?", a: "The D9 is a divisional chart derived from the D1. It refines the strength of planets and is traditionally used for insights on marriage, dharma and the deeper qualities of the soul." },
  { q: "What is Vimshottari Dasha?", a: "A 120-year predictive timeline based on the Moon's Nakshatra at birth. It is divided into Mahadashas (planet lords) and Antardashas (sub-lords) which describe successive life periods." },
];

function Section({ title, defaultOpen = true, children, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-3xl border border-foreground/10 bg-background overflow-hidden" data-testid={testId}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-5 md:p-6 hover:bg-foreground/5 transition-colors">
        <h3 className="font-serif text-xl md:text-2xl text-left">{title}</h3>
        {open ? <ChevronDown className="w-5 h-5 text-foreground/40" /> : <ChevronRight className="w-5 h-5 text-foreground/40" />}
      </button>
      {open && <div className="p-5 md:p-6 pt-0 md:pt-0">{children}</div>}
    </div>
  );
}

function BigStat({ label, value, sub }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-3">
      <div className="text-[10px] uppercase tracking-widest text-[#F5E8D8]/50">{label}</div>
      <div className="mt-1 font-serif text-xl md:text-2xl">{value}</div>
      {sub && <div className="text-[10px] text-[#F5E8D8]/60 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function KundaliGenerator() {
  const [chart, setChart] = useState(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handle = async (payload) => {
    setBusy(true);
    setChart(null);
    try {
      const { data } = await api.post("/kundali/generate", payload);
      setChart(data);
      setTimeout(() => document.getElementById("k-result")?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate the chart. Please try again.");
    } finally { setBusy(false); }
  };

  const downloadPDF = async () => {
    if (!chart?.lead_id) return;
    setDownloading(true);
    try {
      const r = await fetch(`${API_BASE}/kundali/pdf/${chart.lead_id}`);
      if (!r.ok) throw new Error("pdf");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kundali-${(chart.birth.name || "yourastrohub").toLowerCase().replace(/\s+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download PDF. Please try again.");
    } finally { setDownloading(false); }
  };

  return (
    <div data-testid="page-kundali" className="min-h-screen">
      <Seo
        title="Free Vedic Kundali Generator | YourAstroHub"
        description="Generate your free Vedic Kundali with Janam Rashi, Nakshatra, Pada, planetary positions, D1, D9 and Vimshottari Dasha. Swiss Ephemeris powered."
        keywords="Free Kundali, Vedic Kundali Generator, Janam Kundali, Birth Chart, Navamsa, Vimshottari Dasha, Lahiri Ayanamsa"
        path="/kundali-generator"
      />

      <section className="pt-10 pb-6">
        <div className="max-w-3xl mx-auto px-6 md:px-8 text-center">
          <div className="eyebrow mb-3">Free Vedic Tool</div>
          <h1 className="font-serif text-4xl md:text-6xl leading-[1.05]">Free Vedic <span className="gold-text italic">Kundali</span> Generator</h1>
          <p className="mt-4 text-foreground/70 text-base md:text-lg">
            Generate your Vedic birth chart, Janam Rashi, Nakshatra, planetary positions and Vimshottari Dasha — computed with Swiss Ephemeris using the sidereal (Lahiri) zodiac.
          </p>
        </div>
      </section>

      <section className="pb-10">
        <div className="max-w-2xl mx-auto px-6 md:px-8">
          <BirthDetailsForm onSubmit={handle} busy={busy} ctaLabel="Generate my Kundali" tool="kundali" />
        </div>
      </section>

      {chart && (
        <section id="k-result" className="pb-16">
          <div className="max-w-6xl mx-auto px-6 md:px-8 space-y-6">
            {/* Header result card */}
            <div className="rounded-3xl p-6 md:p-10 border-2 border-[#D4AF37]/50 bg-[#0B1F3A] text-[#F5E8D8] relative overflow-hidden" data-testid="k-header">
              <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#D4AF37]/15 rounded-full blur-3xl" />
              <div className="relative">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="eyebrow text-[#F5E8D8]/60">Your Vedic Chart</div>
                    <h2 className="font-serif text-3xl md:text-5xl mt-2 gold-text">Lagna: {chart.lagna.sign}</h2>
                    <div className="text-[#F5E8D8]/70 mt-1">
                      {chart.birth.name && <span>{chart.birth.name} · </span>}
                      {chart.birth.local_datetime.replace("T", " ").slice(0, 16)} · {chart.birth.birthplace}
                    </div>
                    <div className="text-xs text-[#F5E8D8]/50 mt-1">
                      TZ used: <b>{chart.birth.timezone}</b> · UTC: {chart.birth.utc_datetime.slice(0, 16).replace("T", " ")}
                    </div>
                  </div>
                  <Button onClick={downloadPDF} disabled={downloading} data-testid="k-download-pdf"
                    className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold">
                    <Download className="w-4 h-4 mr-2" />{downloading ? "Preparing…" : "Download PDF"}
                  </Button>
                </div>
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                  <BigStat label="Lagna" value={chart.lagna.sign} sub={chart.lagna.dms} />
                  <BigStat label="Nakshatra (Lagna)" value={`${chart.lagna.nakshatra} · P${chart.lagna.pada}`} sub={chart.lagna.nakshatra_lord} />
                  <BigStat label="Janam Rashi" value={chart.moon.sign} sub={chart.moon.dms} />
                  <BigStat label="Janam Nakshatra" value={`${chart.moon.nakshatra} · P${chart.moon.pada}`} sub={chart.moon.nakshatra_lord} />
                </div>
              </div>
            </div>

            {/* Charts */}
            <Section title="D1 (Rashi) & D9 (Navamsa) Charts" testId="s-charts">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-4 rounded-2xl bg-[#F4EFE6]/40 dark:bg-[#0F284B]/40">
                  <NorthIndianChart houses={chart.houses} title={`D1 · Rashi (${chart.lagna.sign})`} highlight={1} />
                </div>
                <div className="p-4 rounded-2xl bg-[#F4EFE6]/40 dark:bg-[#0F284B]/40">
                  <NorthIndianChart houses={chart.d9_houses} title={`D9 · Navamsa (${chart.lagna.navamsa_lagna_sign})`} highlight={1} />
                </div>
              </div>
            </Section>

            {/* Planetary Positions */}
            <Section title="Planetary Positions" testId="s-planets">
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm min-w-[720px]">
                  <thead className="bg-[#0B1F3A] text-[#F5E8D8]">
                    <tr>
                      {["Planet", "Sign", "Degree", "House", "Nakshatra", "Pada", "Nak. Lord", "Motion"].map((h) => (
                        <th key={h} className="p-2 text-left font-semibold text-xs uppercase tracking-widest">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {chart.planets.map((p) => (
                      <tr key={p.name} className="border-t border-foreground/10 hover:bg-foreground/5">
                        <td className="p-2 font-semibold">{p.name} <span className="text-[10px] text-foreground/40">{p.abbr}</span></td>
                        <td className="p-2">{p.sign} <span className="text-[10px] text-foreground/40">({p.sign_lord})</span></td>
                        <td className="p-2 font-mono text-xs">{p.dms}</td>
                        <td className="p-2 text-center">{p.house}</td>
                        <td className="p-2">{p.nakshatra}</td>
                        <td className="p-2 text-center">{p.pada}</td>
                        <td className="p-2 text-xs">{p.nakshatra_lord}</td>
                        <td className="p-2 text-center">
                          {p.retrograde ? <span className="text-rose-500 font-bold text-xs">R</span> : <span className="text-emerald-600 text-xs">D</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 text-[11px] text-foreground/50">
                <b>Legend:</b> R = Retrograde, D = Direct. Rahu &amp; Ketu are lunar nodes (mode: {chart.settings.node_mode}). Ketu is set opposite Rahu (180°) — the classical Jyotish convention.
              </div>
            </Section>

            {/* Vimshottari Dasha */}
            <Section title="Vimshottari Dasha" testId="s-dasha">
              <div className="rounded-2xl bg-[#F4EFE6]/40 dark:bg-[#0F284B]/40 p-4 mb-4">
                <div className="text-xs text-foreground/60">Current Mahadasha</div>
                <div className="mt-1 flex flex-wrap items-baseline gap-3">
                  <span className="font-serif text-3xl gold-text">{chart.vimshottari.current_mahadasha.lord}</span>
                  <span className="text-sm text-foreground/70">
                    {chart.vimshottari.current_mahadasha.start.slice(0, 10)} → {chart.vimshottari.current_mahadasha.end.slice(0, 10)}
                  </span>
                </div>
                <div className="text-[11px] text-foreground/50 mt-1">
                  Balance at birth: <b>{chart.vimshottari.balance_years_at_birth} years</b> of {chart.vimshottari.moon_nakshatra_lord}
                </div>
              </div>

              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm min-w-[560px]">
                  <thead className="bg-[#0B1F3A] text-[#F5E8D8]">
                    <tr>{["Mahadasha", "Start", "End", "Years"].map((h) => <th key={h} className="p-2 text-left font-semibold text-xs uppercase tracking-widest">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {chart.vimshottari.mahadashas.map((md, i) => (
                      <tr key={i} className={`border-t border-foreground/10 ${md.lord === chart.vimshottari.current_mahadasha.lord ? "bg-[#D4AF37]/10" : ""}`}>
                        <td className="p-2 font-semibold">{md.lord}{md.partial && <span className="ml-2 text-[10px] text-foreground/50">(partial)</span>}</td>
                        <td className="p-2 font-mono text-xs">{md.start.slice(0, 10)}</td>
                        <td className="p-2 font-mono text-xs">{md.end.slice(0, 10)}</td>
                        <td className="p-2">{md.years}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <details className="mt-4 rounded-2xl border border-foreground/10 p-4">
                <summary className="cursor-pointer text-sm font-semibold">Current Antardashas (within {chart.vimshottari.current_mahadasha.lord})</summary>
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-sm min-w-[420px]">
                    <thead><tr className="text-xs text-foreground/60">{["Antardasha", "Start", "End"].map((h) => <th key={h} className="p-2 text-left">{h}</th>)}</tr></thead>
                    <tbody>
                      {chart.vimshottari.current_antardashas.map((ad, i) => (
                        <tr key={i} className="border-t border-foreground/10">
                          <td className="p-2">{ad.lord}</td>
                          <td className="p-2 font-mono text-xs">{ad.start.slice(0, 10)}</td>
                          <td className="p-2 font-mono text-xs">{ad.end.slice(0, 10)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </Section>

            {/* Calculation settings */}
            <Section title="Calculation Settings" defaultOpen={false} testId="s-settings">
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div className="flex justify-between border-b border-foreground/5 pb-1"><dt className="text-foreground/60">Zodiac</dt><dd>{chart.settings.zodiac}</dd></div>
                <div className="flex justify-between border-b border-foreground/5 pb-1"><dt className="text-foreground/60">Ayanamsa</dt><dd>{chart.settings.ayanamsa} ({chart.settings.ayanamsa_value_deg}°)</dd></div>
                <div className="flex justify-between border-b border-foreground/5 pb-1"><dt className="text-foreground/60">Ephemeris</dt><dd>{chart.settings.ephemeris}</dd></div>
                <div className="flex justify-between border-b border-foreground/5 pb-1"><dt className="text-foreground/60">Node mode</dt><dd className="capitalize">{chart.settings.node_mode}</dd></div>
                <div className="flex justify-between border-b border-foreground/5 pb-1"><dt className="text-foreground/60">Houses</dt><dd>Whole-sign (Lagna anchored)</dd></div>
                <div className="flex justify-between border-b border-foreground/5 pb-1"><dt className="text-foreground/60">Julian Day</dt><dd className="font-mono text-xs">{chart.birth.julian_day_ut}</dd></div>
              </dl>
            </Section>

            {/* Disclaimer */}
            <p className="text-xs text-foreground/50 italic text-center max-w-2xl mx-auto">
              Astrology is provided for informational and traditional Jyotish purposes. The results generated by this tool should not be treated as medical, legal, financial or other professional advice.
            </p>

            <LeadFooter leadId={chart.lead_id} />
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-12 bg-[#F4EFE6]/50 dark:bg-[#0F284B]/50 border-t border-foreground/10">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <h2 className="font-serif text-3xl md:text-4xl text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {K_FAQ.map((f, i) => (
              <details key={i} className="rounded-2xl border border-foreground/10 bg-background p-5 group" data-testid={`k-faq-${i}`}>
                <summary className="font-semibold cursor-pointer flex items-start justify-between gap-3 marker:hidden">
                  <span>{f.q}</span>
                  <span className="text-[#D4AF37] text-xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm text-foreground/70">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
